<!--
  烂尾单分析导入窗口

  模板列（与导出的列头逐字一致，可「导出 → 改 → 导入」）：
  客户 / 项目 / 联系人 / 原因 / 原因详细说明 / 解决方案
  其中 客户 / 项目 / 原因 填名称，由后端反查成编号；项目必须在所选客户下唯一匹配。
-->
<template>
  <Dialog v-model="dialogVisible" title="烂尾单分析导入" width="480px">
    <el-upload
      ref="uploadRef"
      v-model:file-list="fileList"
      :auto-upload="false"
      :disabled="formLoading"
      :limit="1"
      :on-exceed="handleExceed"
      accept=".xlsx, .xls"
      action="none"
      drag
    >
      <Icon icon="ep:upload" />
      <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      <template #tip>
        <div class="el-upload__tip text-center">
          <div class="el-upload__tip">
            <el-checkbox v-model="updateSupport" />
            是否更新已经存在的项目数据（同一客户下「项目」重复）
          </div>
          <span>仅允许导入 xls、xlsx 格式文件。</span>
          <el-link
            :underline="false"
            style="font-size: 12px; vertical-align: baseline"
            type="primary"
            @click="importTemplate"
          >
            下载模板
          </el-link>
        </div>
      </template>
    </el-upload>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { OrderStallAnalysisApi } from '@/api/crm/orderStallAnalysis'
import download from '@/utils/download'
import type { UploadUserFile } from 'element-plus'

defineOptions({ name: 'StallAnalysisImportForm' })

const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const formLoading = ref(false) // 表单的加载中
const uploadRef = ref()
const fileList = ref<UploadUserFile[]>([]) // 文件列表
const updateSupport = ref(false) // 是否更新已经存在的数据

/** 打开弹窗 */
const open = async () => {
  dialogVisible.value = true
  await resetForm()
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const submitForm = async () => {
  if (fileList.value.length == 0) {
    message.error('请上传文件')
    return
  }

  formLoading.value = true
  try {
    const formData = new FormData()
    formData.append('file', fileList.value[0].raw as Blob)
    const res = await OrderStallAnalysisApi.importOrderStallAnalysis(formData, updateSupport.value)
    submitFormSuccess(res)
  } catch {
    submitFormError()
  } finally {
    formLoading.value = false
  }
}

/** 文件上传成功 */
const emits = defineEmits(['success'])
const submitFormSuccess = (response: any) => {
  if (response.code !== 0) {
    message.error(response.msg)
    formLoading.value = false
    return
  }
  // 拼接提示语
  const data = response.data
  let text = ''
  const createNames: string[] = data.createProjectNames || []
  text += '新增成功数量：' + createNames.length + ';'
  for (const projectName of createNames) {
    text += '< ' + projectName + ' >'
  }
  const updateNames: string[] = data.updateProjectNames || []
  text += '更新成功数量：' + updateNames.length + ';'
  for (const projectName of updateNames) {
    text += '< ' + projectName + ' >'
  }
  const failureNames = data.failureProjectNames || {}
  text += '导入失败数量：' + Object.keys(failureNames).length + ';'
  for (const projectName in failureNames) {
    text += '< ' + projectName + ': ' + failureNames[projectName] + ' >'
  }
  message.alert(text)
  formLoading.value = false
  dialogVisible.value = false
  // 发送操作成功的事件
  emits('success')
}

/** 上传错误提示 */
const submitFormError = (): void => {
  message.error('上传失败，请您重新上传！')
  resetForm()
}

/** 重置表单 */
const resetForm = async () => {
  // 重置上传状态和文件
  fileList.value = []
  updateSupport.value = false
  await nextTick()
  uploadRef.value?.clearFiles()
}

/** 文件数超出提示 */
const handleExceed = (): void => {
  message.error('最多只能上传一个文件！')
}

/** 下载模板操作 */
const importTemplate = async () => {
  const res = await OrderStallAnalysisApi.getOrderStallAnalysisImportTemplate()
  download.excel(res, '烂尾单分析导入模板.xlsx')
}
</script>
